
import { useGetDataLazy, useGetDataQuery } from "../../../../../utils/react-query-hooks/getData";
import { useGetTagsV3 } from "../../../../../utils/react-query-hooks/tags";
import { useGetTpl } from "../../../../../utils/react-query-hooks/tpls";
import { useGetUsers } from "../../../../../utils/react-query-hooks/users";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { generateOptions, getText } from "oolib";
import { convertFilterOptionsByConfIntoFindQuery } from "../utils/convertFilterOptionsByConfIntoFindQuery";
import { getTagTypeTitle} from '../utils/getTagTypeTitle'
import { buildListingPageQueryForKS } from "@/utils/okf-ks-utils/buildListingPageQueryForKS";
import { defaultSortConfig } from "@/pageTpls/PublishedListing/config/defaults";
import { useGetAllUsersLazy } from "@/utils/react-query-hooks/users/useGetAllUsersLazy";
import { defaultUserProfilesSortConfig } from "@/pageTpls/ProfilesListing/configs/defaultUserProfilesSortConfig";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";


export const fetchFilterOptionsConfigs = {
  contentTagType: {
    hook: useGetDataLazy,
    getArgs: ({
      filterConfig,
      enableDataFetch,
      contentTypes,
      activeFilters,
      searchTerm,
      disableLimit,
    }) => [
      {
        contentTypes: [filterConfig.source.tagType],
        findQuery: {
         ...( {kp_published_status:"published"}),
          ...(filterConfig.source.findQuery || {}),

          ...(filterConfig.source.filterOptionsBy //read how this works inside the function definition
            ? convertFilterOptionsByConfIntoFindQuery({
                activeFilters,
                thisFilterConfig: filterConfig,
                filterOptionsBy: filterConfig.source.filterOptionsBy,
              })
            : {}),
        },
        queryOptions: {
          enabled: enableDataFetch === true,
        },
        limit: 20,
        disableLimit,
        // pagination: filterConfig.lazyLoad ? true : false,
        searchTerm,
        ksConfig: buildListingPageQueryForKS({
            keyword: searchTerm,
            config: { fields: ["main.title.autoComplete"] },
            activeSort: defaultSortConfig[0].sort,
        }),
        activeSort: { "main.title": 1 },
      },
    ],
    convertToOptions: ({ data }) => {
      return data && (data?.map((d) => ({
        display: getText(d.main?.title),
        value: d?._id,
      })))
    },
    getFilterDisplay: ({filterConfig}) => {
      return filterConfig.display || getTagTypeTitle({tagType: filterConfig.source.tagType})
    }
  },
  // "Filter by Document" — lists documents of the datasets in view (or an explicit
  // set on the config) by title, valued by _id. Mirrors contentTagType's fetch but
  // points at the ambient `contentTypes` instead of a single tag collection. The
  // filter's target is its own type ("documentsType"); the backend resolves which
  // field those ids match per query — _id on the contributions collections,
  // meta.documentId on annotations/chunks.
  documentsType: {
    hook: useGetDataLazy,
    getArgs: ({
      filterConfig,
      enableDataFetch,
      contentTypes,
      searchTerm,
      disableLimit,
    }) => [
      {
        contentTypes: filterConfig.source?.contentTypes || contentTypes,
        findQuery: {
          kp_published_status: "published",
          ...(filterConfig.source?.findQuery || {}),
        },
        queryOptions: {
          enabled: enableDataFetch === true,
        },
        limit: 20,
        disableLimit,
        searchTerm,
        ksConfig: buildListingPageQueryForKS({
          keyword: searchTerm,
          config: { fields: ["main.title.autoComplete"] },
          activeSort: defaultSortConfig[0].sort,
        }),
        activeSort: { "main.title": 1 },
      },
    ],
    convertToOptions: ({ data }) => {
      return data && data.map((d) => ({
        display: getText(d.main?.title),
        value: d?._id,
      }));
    },
    getFilterDisplay: ({ filterConfig }) => filterConfig.display || "Documents",
  },
  usersType: {
    hook: useGetAllUsersLazy,
    getArgs: ({ filterConfig, enableDataFetch, searchTerm, disableLimit }) => [
      {
        // tplId: filterConfig.source.contentType,
        findQuery:filterConfig.source.profileTypes?{profileType:{$in:filterConfig.source.profileTypes}}:{},
        enabled: enableDataFetch === true,
        limit: 20,
        disableLimit,
        searchTerm,
        ksConfig: searchTerm &&
          buildListingPageQueryForKS({
            keyword: searchTerm,
            config: {
              fields: [
                "name.autoComplete",
                "email.autoComplete",
                "phone.number.autoComplete",
              ],
            },
            activeSort: defaultUserProfilesSortConfig[0].sort,
          }),
         activeSort: { "name": 1 },

      },
    ],
    convertToOptions: ({ data, filterConfig }) => {
      const options = data.map((d) => ({
        display: d.name,
        value: d._id,
      }));
      const pinned = filterConfig?.source?.pinnedOptions;
      return pinned?.length ? [...pinned, ...options] : options;
    },
    getFilterDisplay: ({filterConfig}) => {
      return filterConfig.display || (filterConfig.source.profileTypes?.length > 1
        ? 'Users'
        : getTagTypeTitle({tagType: filterConfig.source.profileTypes[0]}))
    }
  },
  authorsType: {
    hook: useGetDataQuery,
    getArgs: ({ filterConfig, enableDataFetch, contentTypes }) => [
      {
        contentTypes,
        findQuery: { kp_published_status: "published" },
        countData: false,
        useAggregation: {
          groupByWithLookup: {
            field: "meta.kp_contributed_by",
            lookupFrom: "users",
            displayField: "name",
          }
        },
        queryOptions: {
          enabled: enableDataFetch === true,
        },
      },
    ],
    convertToOptions: ({ data }) => {
      return data.data;
    },
    getFilterDisplay: ({filterConfig}) => {
      return filterConfig.display || (filterConfig.source.profileTypes?.length > 1
        ? 'Users'
        : getTagTypeTitle({tagType: filterConfig.source.profileTypes[0]}))
    },
    frontendSearch: true
  },
  tplType: {
    hook: useGetTpl,
    getArgs: ({ filterConfig, enableDataFetch }) => [
      filterConfig.source.contentType,
      {       
        enabled: enableDataFetch === true
       },
    ],
    convertToOptions: ({ data, filterConfig }) => {
      const allBlocks = extractAllBlocksFromTpl({ tpl: data });

      const block = allBlocks.find(
        (block) => block.valuePath === filterConfig.source.valuePath
      );

      if(!block) return []

    
      const options = block?.props?.options?.sort((a, b) => a.display.toString().localeCompare(b.display.toString()))

      // For the block where injectOtherProp is set to true ( ex- checkboxList), ensure that we explicitly include the other option in the filter options for this block.
      if (block.props.injectOtherOption) {
        return [...options, { display: "Other", value: "other" }];
      } else {
        return options;
      }
      
    },
    getFilterDisplay: ({ filterConfig }) => filterConfig.display,
    frontendSearch: true
  },
  staticType: {
    hook: (filterConfig, theme) => {
      const options = filterConfig.source.options;
      const optionsFn = filterConfig.source.optionsFn;
      return {
        status: "success",
        data: generateOptions({ options, optionsFn,theme }),
      };
    },
    getArgs: ({ filterConfig, theme }) => [filterConfig,theme],
    convertToOptions: ({ data }) => {
      return data?.sort((a, b) => a.display.toString().localeCompare(b.display.toString()));
    },
    getFilterDisplay: ({ filterConfig }) => filterConfig.display,
    frontendSearch: true
  },
  dateRangeType: {
    lightboxConfig: {
      comp: 'DateRangeType',
      props: {}
    },
    getFilterDisplay: ({ filterConfig }) => filterConfig.display
  },
  numberRangeType: {
    hook: useAggrMetricsAcrossDimensions,
    getArgs: ({ filterConfig, enableDataFetch, contentTypes }) => [{
      contentType: contentTypes?.[0],
      dimensions: [filterConfig.source.valuePath],
      metrics: [{ type: 'count', name: 'count' }],
      returnMetadata: true,  // Opt-in to get min/max in response
      queryOptions: { enabled: enableDataFetch },
      vizCardId: `numberRangeFilter_${filterConfig.filterId}`
    }],
    lightboxConfig: {
      comp: 'NumberRangeType',
      props: {}
    },
    // Transform API response into lightbox props
    // Response format: { data: [...], metadata: { dimensions: { [valuePath]: { min, max } } } }
    getLightboxProps: ({ data, filterConfig, status }) => {
      const isLoading = status === 'loading' || status === 'pending';

      if (isLoading || !data) {
        return { loading: true, min: 0, max: 100, aggregatedData: null };
      }

      const valuePath = filterConfig.source?.valuePath;
      const metadata = data?.metadata?.dimensions?.[valuePath];

      return {
        min: metadata?.min ?? 0,
        max: metadata?.max ?? 100,
        step: filterConfig.source?.step ?? 1,  // Allow step to be configured via filterConfig
        loading: false,
        aggregatedData: data  // Full response for bar graph visualization
      };
    },
    getFilterDisplay: ({ filterConfig }) => filterConfig.display
  },
  /**
   * I HAVE A FEELING THIS IS FULLY UNUSED. SO COMMENTING IT OUT, AND WE SEE WHAT HAPPENS
   * essentially, this is only used, for fetching user tags. content tags even if filter type = 'tagType' gets routed to 'contentTagType' (look at getFilterType function)
   * 
   * This is fully redundant, especially considering the new autogen filters approach, which would never
   * route to this case (after getFilterType runs). Which means that, only the legacy tenants could have
   * explicit config routing to this, in case the tagType is 
   */
  // tagType: {
  //   //to deprecate
  //   hook: useGetTagsV3,
  //   getArgs: ({ filterConfig, enableDataFetch, contentTypes, searchTerm }) => [
  //     // to deprecate
  //     filterConfig.source.tagType,
  //     {
  //       tagCategory: "tags",
  //       filters: {
  //         ...(filterConfig.source.findQuery || {}),         
  //       },
  //       enabled: enableDataFetch === true,
  //       searchTerm,
  //       ksConfig: searchTerm &&
  //         buildListingPageQueryForKS({
  //           keyword: searchTerm,
  //           config: { fields: ["main.title.autoComplete"] },
  //           activeSort: defaultSortConfig[0].sort,
  //       }),
  //     },
  //   ],
  //   convertToOptions: ({ data }) => {
  //     const items = data.pages 
  //       ? data.pages.reduce((acc, page) => [...acc, ...(Array.isArray(page) ? page : page.data)], []) 
  //       : data;
      
  //     return items.map((d) => ({
  //       display: d.display,
  //       value: d.tagId,
  //     }));
  //   },
  //   getFilterDisplay: ({filterConfig}) => {
  //     return filterConfig.display || getTagTypeTitle({tagType: filterConfig.source.tagType});
  //   }
  // },
};
